import React, { useState, useEffect } from 'react';
import AdminLayout from '../../components/admin/AdminLayout';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Upload, Download, FileSpreadsheet, CheckCircle, Loader2, AlertCircle, FileText } from 'lucide-react';
import { bulkAPI, categoriesAPI } from '../../services/api';

const AdminBulkUpload = () => {
  const [file, setFile] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const [error, setError] = useState('');

  const handleFileUpload = async (e) => {
    e.preventDefault();
    if (!file) return;

    setIsUploading(true);
    setError('');
    setImportResult(null);
    
    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await bulkAPI.uploadExcel(formData);
      setImportResult({
        success: true,
        message: `Successfully imported ${response.data.products_created} products`,
        products_created: response.data.products_created,
        errors: response.data.errors
      });
      setFile(null);
      // Reset file input
      const fileInput = document.querySelector('input[type="file"]');
      if (fileInput) fileInput.value = '';
    } catch (err) {
      setError(err.response?.data?.detail || 'Upload failed. Please check your file format.');
    } finally {
      setIsUploading(false);
    }
  };

  return (
    <AdminLayout>
      <div className="max-w-4xl">
        <h1 className="text-4xl font-bold text-charcoal mb-2 font-heading">Bulk Upload Products</h1>
        <p className="text-lg text-stone-grey mb-8 font-body">Import multiple products using Excel file</p>

        {/* Error Message */}
        {error && (
          <div className="mb-6 p-4 bg-red-50 border-2 border-red-200 rounded-xl flex items-start gap-3">
            <AlertCircle className="h-5 w-5 text-red-600 flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <p className="text-red-700 font-body font-semibold">Upload Error</p>
              <p className="text-red-600 font-body text-sm">{error}</p>
            </div>
            <button onClick={() => setError('')} className="text-red-600 hover:text-red-800 text-xl">×</button>
          </div>
        )}

        {/* Success Message */}
        {importResult && importResult.success && (
          <div className="mb-6 p-6 bg-green-50 border-2 border-green-200 rounded-xl">
            <div className="flex items-center gap-3 mb-3">
              <CheckCircle className="h-6 w-6 text-green-600" />
              <p className="font-bold text-green-800 font-body text-lg">{importResult.message}</p>
            </div>
            <p className="text-green-700 font-body text-sm">
              Products have been added to your store. Go to Products page to view them.
            </p>
            {importResult.errors && importResult.errors.length > 0 && (
              <div className="mt-4 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                <p className="font-semibold text-amber-800 text-sm mb-1">Warnings:</p>
                {importResult.errors.map((e, i) => <p key={i} className="text-amber-700 text-sm">• {e}</p>)}
              </div>
            )}
          </div>
        )}

        {/* Main Upload Section */}
        <div className="bg-white rounded-xl p-8 border-2 border-soft-sand shadow-soft">
          <div className="flex items-center gap-4 mb-8">
            <div className="w-16 h-16 bg-accent/10 rounded-xl flex items-center justify-center">
              <FileSpreadsheet className="h-8 w-8 text-accent" />
            </div>
            <div>
              <h2 className="text-2xl font-bold text-charcoal font-heading">Upload Excel File</h2>
              <p className="text-stone-grey font-body">Upload .xlsx or .xls file with product data</p>
            </div>
          </div>

          <form onSubmit={handleFileUpload} className="space-y-6">
            {/* File Input */}
            <div className="border-2 border-dashed border-soft-sand rounded-xl p-8 text-center hover:border-accent transition-colors">
              <input
                type="file"
                accept=".xlsx,.xls"
                onChange={(e) => setFile(e.target.files[0])}
                className="hidden"
                id="file-upload"
              />
              <label htmlFor="file-upload" className="cursor-pointer">
                <Upload className="h-12 w-12 mx-auto text-text-muted mb-4" />
                <p className="text-lg font-semibold text-text-heading font-body mb-2">
                  {file ? file.name : 'Click to select file'}
                </p>
                <p className="text-sm text-text-muted font-body">
                  Supports .xlsx and .xls files
                </p>
              </label>
            </div>
            
            {file && (
              <div className="flex items-center gap-3 p-4 bg-accent/5 border border-accent/20 rounded-lg">
                <FileText className="h-5 w-5 text-accent" />
                <span className="font-medium text-text-heading font-body">{file.name}</span>
                <span className="text-sm text-text-muted font-body">({(file.size / 1024).toFixed(1)} KB)</span>
                <button 
                  type="button" 
                  onClick={() => setFile(null)}
                  className="ml-auto text-red-500 hover:text-red-700"
                >
                  Remove
                </button>
              </div>
            )}

            <Button
              type="submit"
              disabled={!file || isUploading}
              className="w-full bg-accent hover:bg-accent-hover text-white py-6 text-lg font-body font-bold"
            >
              {isUploading ? (
                <><Loader2 className="h-5 w-5 mr-2 animate-spin" /> Uploading & Processing...</>
              ) : (
                <><Upload className="h-5 w-5 mr-2" /> Upload Products</>
              )}
            </Button>
          </form>

          {/* Download Template */}
          <div className="mt-8 pt-8 border-t-2 border-soft-sand">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="font-semibold text-text-heading font-body mb-1">Need a template?</h3>
                <p className="text-sm text-text-muted font-body">Download our Excel template with correct column headers</p>
              </div>
              <a href="/product_template.xlsx" download="serenity_product_template.xlsx">
                <Button
                  type="button"
                  variant="outline"
                  className="border-2 border-accent text-accent hover:bg-accent hover:text-white font-body"
                >
                  <Download className="h-5 w-5 mr-2" />
                  Download Template
                </Button>
              </a>
            </div>
          </div>
        </div>

        {/* Instructions */}
        <div className="mt-8 bg-white rounded-xl p-8 border-2 border-soft-sand shadow-soft">
          <h3 className="text-2xl font-bold text-charcoal mb-6 font-heading">How to Use</h3>
          
          <div className="space-y-4">
            <div className="flex gap-4">
              <div className="w-8 h-8 bg-accent text-white rounded-full flex items-center justify-center font-bold flex-shrink-0">1</div>
              <div>
                <p className="font-semibold text-text-heading font-body">Download the template</p>
                <p className="text-text-muted font-body text-sm">Get our Excel template with all required columns</p>
              </div>
            </div>
            
            <div className="flex gap-4">
              <div className="w-8 h-8 bg-accent text-white rounded-full flex items-center justify-center font-bold flex-shrink-0">2</div>
              <div>
                <p className="font-semibold text-text-heading font-body">Fill in your product data</p>
                <p className="text-text-muted font-body text-sm">Add products with name, SKU, category, price, description, and image URLs</p>
              </div>
            </div>
            
            <div className="flex gap-4">
              <div className="w-8 h-8 bg-accent text-white rounded-full flex items-center justify-center font-bold flex-shrink-0">3</div>
              <div>
                <p className="font-semibold text-text-heading font-body">Upload and import</p>
                <p className="text-text-muted font-body text-sm">Upload the file and products will be created automatically</p>
              </div>
            </div>
          </div>

          {/* Column Reference */}
          <div className="mt-8 pt-6 border-t border-soft-sand">
            <h4 className="font-semibold text-text-heading mb-4 font-body">Required Columns:</h4>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mb-6">
              <div className="p-3 bg-soft-sand/30 rounded-lg">
                <code className="text-accent font-mono text-sm">name</code>
                <p className="text-xs text-text-muted mt-1">Product name *</p>
              </div>
              <div className="p-3 bg-soft-sand/30 rounded-lg">
                <code className="text-accent font-mono text-sm">category</code>
                <p className="text-xs text-text-muted mt-1">Category name *</p>
              </div>
              <div className="p-3 bg-soft-sand/30 rounded-lg">
                <code className="text-accent font-mono text-sm">price</code>
                <p className="text-xs text-text-muted mt-1">Price in USD *</p>
              </div>
            </div>

            <h4 className="font-semibold text-text-heading mb-4 font-body">Optional Columns:</h4>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
              <div className="p-3 bg-soft-sand/30 rounded-lg">
                <code className="text-accent font-mono text-sm">sku</code>
                <p className="text-xs text-text-muted mt-1">Unique SKU</p>
              </div>
              <div className="p-3 bg-soft-sand/30 rounded-lg">
                <code className="text-accent font-mono text-sm">brand</code>
                <p className="text-xs text-text-muted mt-1">Brand name</p>
              </div>
              <div className="p-3 bg-soft-sand/30 rounded-lg">
                <code className="text-accent font-mono text-sm">images</code>
                <p className="text-xs text-text-muted mt-1">URLs (comma-sep)</p>
              </div>
              <div className="p-3 bg-soft-sand/30 rounded-lg">
                <code className="text-accent font-mono text-sm">description</code>
                <p className="text-xs text-text-muted mt-1">HTML supported</p>
              </div>
              <div className="p-3 bg-soft-sand/30 rounded-lg">
                <code className="text-accent font-mono text-sm">sale_price</code>
                <p className="text-xs text-text-muted mt-1">Discounted price</p>
              </div>
              <div className="p-3 bg-soft-sand/30 rounded-lg">
                <code className="text-accent font-mono text-sm">stock_quantity</code>
                <p className="text-xs text-text-muted mt-1">Inventory count</p>
              </div>
              <div className="p-3 bg-soft-sand/30 rounded-lg">
                <code className="text-accent font-mono text-sm">video_url</code>
                <p className="text-xs text-text-muted mt-1">Video link</p>
              </div>
              <div className="p-3 bg-soft-sand/30 rounded-lg">
                <code className="text-accent font-mono text-sm">is_new</code>
                <p className="text-xs text-text-muted mt-1">TRUE/FALSE</p>
              </div>
            </div>

            <h4 className="font-semibold text-text-heading mb-4 font-body">Specification Columns:</h4>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
              <div className="p-3 bg-muted-green/10 rounded-lg border border-muted-green/20">
                <code className="text-muted-green font-mono text-sm">size</code>
                <p className="text-xs text-text-muted mt-1">e.g., "6 inches"</p>
              </div>
              <div className="p-3 bg-muted-green/10 rounded-lg border border-muted-green/20">
                <code className="text-muted-green font-mono text-sm">color</code>
                <p className="text-xs text-text-muted mt-1">e.g., "Green"</p>
              </div>
              <div className="p-3 bg-muted-green/10 rounded-lg border border-muted-green/20">
                <code className="text-muted-green font-mono text-sm">material</code>
                <p className="text-xs text-text-muted mt-1">e.g., "Soft Fabric"</p>
              </div>
              <div className="p-3 bg-muted-green/10 rounded-lg border border-muted-green/20">
                <code className="text-muted-green font-mono text-sm">weight</code>
                <p className="text-xs text-text-muted mt-1">e.g., "0.5 lbs"</p>
              </div>
              <div className="p-3 bg-muted-green/10 rounded-lg border border-muted-green/20">
                <code className="text-muted-green font-mono text-sm">dimensions</code>
                <p className="text-xs text-text-muted mt-1">e.g., "6x4x4 in"</p>
              </div>
              <div className="p-3 bg-muted-green/10 rounded-lg border border-muted-green/20">
                <code className="text-muted-green font-mono text-sm">bullet_points</code>
                <p className="text-xs text-text-muted mt-1">Separated by |</p>
              </div>
              <div className="p-3 bg-muted-green/10 rounded-lg border border-muted-green/20 md:col-span-2">
                <code className="text-muted-green font-mono text-sm">specifications</code>
                <p className="text-xs text-text-muted mt-1">Format: key:value|key:value</p>
              </div>
            </div>

            <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg">
              <p className="font-semibold text-amber-800 text-sm mb-2">💡 Tips for bullet_points & specifications:</p>
              <ul className="text-xs text-amber-700 space-y-1">
                <li><b>bullet_points:</b> Soft fabric|Machine washable|Safe for all ages</li>
                <li><b>specifications:</b> Age Range:3+|Care:Machine Wash|Made In:USA</li>
                <li><b>description:</b> Use &lt;p&gt;text&lt;/p&gt; and &lt;ul&gt;&lt;li&gt;bullet&lt;/li&gt;&lt;/ul&gt; for formatting</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </AdminLayout>
  );
};

export default AdminBulkUpload;
